/**
 * FlagUserModal
 *
 * Lets any admin file a flag against a peer/user for super-admin review.
 * The flag itself doesn't take immediate action — it surfaces the concern
 * in the super-admin Flag Inbox + writes an audit row + (Phase 2) sends
 * an in-app notification.
 */

import React, { useState } from 'react';
import { FiX, FiFlag } from 'react-icons/fi';
import { userFlagsAPI } from '../../services/api';
import Button from '../common/Button';

export default function FlagUserModal({ user, onClose, onFlagged }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      setError('Reason is required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await userFlagsAPI.create(user._id, reason.trim());
      if (res?.success) {
        if (typeof onFlagged === 'function') onFlagged(res.data?.flag);
        onClose();
      } else {
        setError(res?.message || 'Failed to file flag.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to file flag.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center"
      style={{ background: 'rgba(8,17,31,0.55)' }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-[14px] max-w-[480px] w-full mx-4 shadow-2xl"
        style={{ boxShadow: '0 25px 50px -12px rgba(8,17,31,.25)' }}
      >
        <div className="px-5 py-4 border-b flex items-center gap-2" style={{ borderColor: '#e8e2d6', background: 'linear-gradient(180deg,#fdf6e6 0%,#ffffff 100%)' }}>
          <FiFlag className="text-[16px]" style={{ color: '#b8973a' }} />
          <h3 className="text-[16px] font-bold text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Flag for Super Admin Review
          </h3>
          <button type="button" onClick={onClose} className="ml-auto w-7 h-7 rounded-full flex items-center justify-center text-[#7a7060] hover:bg-[#f8f5ef]">
            <FiX className="text-[14px]" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <div className="text-[12px] text-[#08111f]">
            <div>
              You're flagging <strong>{user.fullName}</strong>{' '}
              <span className="text-[#7a7060]">({user.email})</span> for super-admin review.
            </div>
            <div className="text-[11.5px] text-[#7a7060] mt-1">
              No action is taken on the account immediately. A super admin will review and decide whether to clear the flag or escalate.
            </div>
          </div>

          <div>
            <label className="block text-[10.5px] font-semibold uppercase tracking-wider text-[#7a7060] mb-1">
              Reason for flagging *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="What is the concern? Be specific — what did you notice, when, and why does it warrant super-admin review?"
              rows={5}
              maxLength={1000}
              className="w-full border-[1.5px] rounded-[9px] px-3 py-2 text-[12.5px] outline-none transition-shadow focus:shadow-sm focus:border-[#145f5f40]"
              style={{ borderColor: '#ddd6c6' }}
            />
            <div className="text-[10px] text-[#9a9080] text-right mt-0.5">{reason.length}/1000</div>
          </div>

          {error && (
            <div className="text-[12px] px-3 py-2 rounded-lg border bg-[#f2e9e6] border-[#e6d2cb] text-[#9a564a]">{error}</div>
          )}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-end gap-2" style={{ borderColor: '#e8e2d6' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="gold"
            loading={busy}
            disabled={!reason.trim()}
          >
            Submit Flag
          </Button>
        </div>
      </form>
    </div>
  );
}
